import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import SystemsHeader from '../../components/SystemsHeader';
import SystemsFooter from '../../components/SystemsFooter';
import SeoHead from '../../components/SeoHead';
import CrossDivisionRail from '../../components/CrossDivisionRail';
import { useSystemsCatalog } from '../../context/SystemsCatalogContext';
import { getCtaCopy } from '../../data/systemsCatalog';
import { getBreadcrumbSchema } from '../../data/breadcrumbSchema';
import { Check, ArrowRight, Users, Building2, Briefcase } from 'lucide-react';

export default function ServiceDetail() {
  const { slug } = useParams();
  const { getService, loading } = useSystemsCatalog();

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <SystemsHeader />
        <main className="py-32 text-center text-gray-500">Loading service…</main>
        <SystemsFooter />
      </div>
    );
  }
  const svc = getService(slug);
  if (!svc) return <Navigate to="/systems/solutions" replace />;
  const copy = getCtaCopy(svc);

  return (
    <div className="min-h-screen bg-white">
      <SeoHead
        path={`/systems/solutions/${slug}`}
        title={`${svc.name} — ZHELTA Systems`}
        description={svc.description}
        jsonLd={getBreadcrumbSchema([
          { name: 'Home', path: '/' },
          { name: 'Systems', path: '/systems' },
          { name: 'Solutions', path: '/systems/solutions' },
          { name: svc.name, path: `/systems/solutions/${slug}` },
        ])}
      />
      <SystemsHeader />
      <main>
        {/* Hero */}
        <section className="relative overflow-hidden bg-[radial-gradient(ellipse_90%_70%_at_50%_20%,#ffffff_0%,#FDFCFB_45%,#f5f1ea_100%)] py-20 md:py-28">
          <div className="zh-container max-w-6xl">
            <div className={`grid gap-10 items-center ${svc.image_url ? 'md:grid-cols-[1.1fr_0.9fr]' : ''}`}>
              <div className="max-w-3xl">
                <span className="inline-block text-[11px] font-extrabold uppercase tracking-[0.2em] text-[#c9a227]">{svc.category}</span>
                <h1 className="mt-4 text-4xl sm:text-5xl md:text-6xl font-extrabold tracking-tight text-gray-900 leading-[1.05]">{svc.name}</h1>
                <p className="mt-6 text-[18px] text-gray-600 leading-relaxed">{svc.description}</p>
                <div className="mt-8 flex flex-wrap gap-3">
                  <Link to={`/systems/solutions/${slug}/pricing`} data-testid="service-subscribe" className="px-6 py-3 rounded-full bg-[#f5a623] text-black text-[13px] font-semibold uppercase tracking-wider hover:brightness-110 transition inline-flex items-center gap-2">
                    {svc.billing === 'subscription' ? 'Subscribe Now' : 'Buy Now'} <ArrowRight className="w-4 h-4" />
                  </Link>
                  <Link to={`/systems/book-demo?service=${slug}`} data-testid="service-book-demo" className="px-6 py-3 rounded-full bg-white border-2 border-zinc-300 text-zinc-900 text-[13px] font-semibold uppercase tracking-wider hover:bg-zinc-900 hover:text-white transition">{copy.bookLabel}</Link>
                  <Link to="/systems/solutions" className="px-6 py-3 rounded-full text-gray-700 hover:text-black text-[13px] font-semibold uppercase tracking-wider transition">Explore Solutions</Link>
                </div>
              </div>
              {svc.image_url && (
                <div className="relative" data-testid="service-hero-image">
                  <div className="absolute -inset-4 rounded-[28px] bg-gradient-to-br from-[#fff8e0] to-transparent blur-2xl opacity-70" aria-hidden />
                  <img
                    src={svc.image_url}
                    alt={svc.name}
                    loading="lazy"
                    className="relative w-full aspect-[4/3] object-cover rounded-2xl border border-[#f0ede5] shadow-[0_30px_60px_-30px_rgba(15,15,16,0.45)]"
                  />
                </div>
              )}
            </div>
          </div>
        </section>

        {/* What this service includes */}
        <section className="py-16 md:py-20 bg-white">
          <div className="zh-container max-w-4xl">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8">What This Service Includes</h2>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {svc.includes?.map((inc, i) => (
                <li key={i} className="flex items-start gap-3 p-4 rounded-xl bg-gray-50 border border-gray-100">
                  <Check className="w-5 h-5 text-[#f5a623] mt-0.5 flex-shrink-0" strokeWidth={3} />
                  <span className="text-[15px] text-gray-800">{inc}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Who this supports */}
        <section className="py-16 md:py-20 bg-[#fafafa] border-t border-gray-100">
          <div className="zh-container max-w-5xl">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-10 text-center">Who This Service Supports</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {[
                { icon: Users, title: 'Individuals', body: 'Reliable support for personal devices, system performance, and security.' },
                { icon: Building2, title: 'Businesses', body: 'Proactive monitoring and management of workplace systems and devices.' },
                { icon: Briefcase, title: 'Organizations', body: 'Scalable IT management designed for teams and operational environments.' },
              ].map((c) => {
                const Icon = c.icon;
                return (
                  <div key={c.title} className="rounded-2xl bg-white border border-gray-200 p-6">
                    <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center mb-4">
                      <Icon className="w-5 h-5 text-[#c9a227]" strokeWidth={2} />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">{c.title}</h3>
                    <p className="text-[14px] text-gray-600 leading-relaxed">{c.body}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Next step */}
        <section className="py-16 md:py-24 bg-white border-t border-gray-100">
          <div className="zh-container max-w-3xl text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Next Step</h2>
            <p className="text-[17px] text-gray-600 mb-8">Speak with a ZHELTA Systems specialist to learn how {svc.name} can support your technology environment.</p>
            <div className="flex flex-wrap gap-3 justify-center">
              <Link to={`/systems/solutions/${slug}/pricing`} className="px-6 py-3 rounded-full bg-[#f5a623] text-black text-[13px] font-semibold uppercase tracking-wider hover:brightness-110 transition inline-flex items-center gap-2">
                {svc.billing === 'subscription' ? 'Subscribe Now' : 'Buy Now'} <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to={`/systems/book-demo?service=${slug}`} className="px-6 py-3 rounded-full bg-white border-2 border-zinc-300 text-zinc-900 text-[13px] font-semibold uppercase tracking-wider hover:bg-zinc-900 hover:text-white transition">{copy.bookLabel}</Link>
              <Link to={`/systems/custom-build/${slug}`} className="px-6 py-3 rounded-full text-gray-700 hover:text-black text-[13px] font-semibold uppercase tracking-wider transition">Custom Build</Link>
            </div>
          </div>
        </section>
        <CrossDivisionRail slug={slug} kind="systems_service" />
      </main>
      <SystemsFooter />
    </div>
  );
}
